/**
 * One read, with its four honest outcomes.
 *
 * The console used to have two: data, or no data. Every failure — an expired session, a 500, a
 * dropped connection — was turned into an empty collection by a bare `catch`, so «the server did not
 * answer» and «there is nothing here» drew the same screen. An administrator watching a register go
 * empty has no way to tell a deployment problem from a deletion, and that is the worst possible
 * moment to be guessing.
 *
 * So a read is `loading`, `ready`, or `error`, and an error says which kind:
 *
 *   - `session`   the session is gone. The shell's own auth flow owns this, not a toast.
 *   - `forbidden` the role may not perform this read. A section, not a blank console.
 *   - `server`    the server or the network failed. Visible, with a retry.
 *
 * And one more distinction the old code could not express: when a REFRESH fails, the previous answer
 * is still on screen. It is not current any more, so `stale` is set and the screen says so instead of
 * presenting old numbers as today's.
 *
 * Races are resolved by token, not by arrival. Typing in the search box starts a read per keystroke
 * and they do not come back in order; an answer whose token is no longer the wanted one is dropped,
 * so the slow answer to «ay» cannot overwrite the fast answer to «ayşe».
 *
 * And staleness belongs to ONE question. An earlier answer is kept only while the question is
 * unchanged — a failed refresh of the record on screen still shows that record, marked stale. Move to
 * a different record and the previous one is gone: carrying it across would mean showing A's data, and
 * A's buttons, on B's page, which is a worse failure than a blank moment.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isApiError } from '../../api/errors';
import { useReadsRevision } from './queries-context';

export type ReadFailureKind = 'session' | 'forbidden' | 'server';

export interface ReadFailure {
  kind: ReadFailureKind;
  /** The backend's own message where there is one; never a stack trace. */
  message: string;
}

export interface ReadResult<T> {
  /** The most recent answer, or null before the first one arrives. */
  value: T | null;
  status: 'loading' | 'ready' | 'error';
  /** True when `value` is an EARLIER answer and the latest attempt failed. */
  stale: boolean;
  error: ReadFailure | null;
  /** Ask again. Safe to call from a button. */
  retry: () => void;
}

export function readFailureOf(e: unknown): ReadFailure {
  if (isApiError(e)) {
    if (e.status === 401) return { kind: 'session', message: e.message };
    if (e.status === 403) return { kind: 'forbidden', message: e.message };
    return { kind: 'server', message: e.message };
  }
  return {
    kind: 'server',
    message: e instanceof Error && e.message ? e.message : 'Sunucuya ulaşılamadı',
  };
}

const isPromise = <T>(v: T | Promise<T>): v is Promise<T> =>
  typeof (v as { then?: unknown } | null)?.then === 'function';

interface Snapshot<T> {
  token: string;
  value: T | null;
  status: 'loading' | 'ready' | 'error';
  stale: boolean;
  error: ReadFailure | null;
  pending: Promise<T> | null;
}

/**
 * `run` is called during render, once per token.
 *
 * Deliberate: a source that is already in memory must settle in the same paint, or the demo — whose
 * screens never had a loading state and whose pixels are compared against the frozen reference —
 * would grow one. A read is idempotent, so the cost of React calling it twice in a development
 * double-render is one extra GET in development.
 *
 * `token` is the identity of the question. Change it and a new read starts; keep it and nothing is
 * re-fetched on re-render.
 */
export function useRead<T>(token: string, run: () => T | Promise<T>): ReadResult<T> {
  const [attempt, setAttempt] = useState(0);
  /*
   * The question, plus two things that also make it a different question: an explicit retry, and a
   * command having changed the world since it was last asked.
   */
  const revision = useReadsRevision();
  const question = `${token}@${revision}`;
  const wanted = `${question}#${attempt}`;
  const runRef = useRef(run);
  runRef.current = run;
  /**
   * The last answer that arrived, and WHICH question it answered.
   *
   * Keyed, because «keep what is on screen when a refresh fails» and «show the previous record while
   * a different one loads» are not the same thing, and the second is a bug. A revision bump is the
   * same question: the answer it already has is still about this record, so a failed refresh after a
   * command keeps it — marked stale — instead of blanking the screen.
   */
  const lastGood = useRef<{ token: string; value: T } | null>(null);
  const carried = lastGood.current?.token === token ? lastGood.current.value : null;

  /*
   * Exactly once per question — and `useMemo` alone does not guarantee that.
   *
   * Starting the work during render is deliberate: it is the only way a source that is already in
   * memory (the demo's) settles in the paint that asked for it, so the screens that never had a
   * loading state still do not have one. But React re-invokes a render — twice in development under
   * `StrictMode`, and again whenever it discards a render — and an earlier shape therefore issued two
   * requests per keystroke and left the second promise with nobody listening to it, which surfaced as
   * an unhandled rejection the moment a read failed.
   *
   * So the started work is cached by question in a ref. A second invocation for the same question gets
   * the same promise back; a new question starts new work.
   */
  const startedRef = useRef<Snapshot<T> | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the question is the identity, not `beginWork`
  const started = useMemo<Snapshot<T>>(() => {
    if (startedRef.current?.token === wanted) return startedRef.current;
    const snap = beginWork();
    startedRef.current = snap;
    return snap;
  }, [wanted]);

  function beginWork(): Snapshot<T> {
    try {
      const result = runRef.current();
      if (!isPromise(result)) {
        lastGood.current = { token, value: result };
        return { token: wanted, value: result, status: 'ready', stale: false, error: null, pending: null };
      }
      /* Marked handled here as well: an unmount before the effect runs would otherwise leave the
         rejection to the runtime, which reports it as an unhandled error in the page. */
      result.catch(() => undefined);
      return {
        token: wanted,
        value: carried,
        status: 'loading',
        /* Whatever is shown while a new answer is in flight is, by definition, not the new answer. */
        stale: carried !== null,
        error: null,
        pending: result,
      };
    } catch (e) {
      return {
        token: wanted,
        value: carried,
        status: 'error',
        stale: carried !== null,
        error: readFailureOf(e),
        pending: null,
      };
    }
  }

  const [settled, setSettled] = useState<Snapshot<T> | null>(null);
  /* An answer to an older question is not an answer to this one. */
  const active = settled !== null && settled.token === wanted ? settled : started;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `token` rides along with `started`
  useEffect(() => {
    const pending = started.pending;
    if (pending === null) return;
    let cancelled = false;
    pending.then(
      (value) => {
        if (cancelled) return;
        lastGood.current = { token, value };
        setSettled({
          token: started.token,
          value,
          status: 'ready',
          stale: false,
          error: null,
          pending: null,
        });
      },
      (e: unknown) => {
        if (cancelled) return;
        /* A failed refresh keeps what was on screen — of THIS question — and marks it not current. */
        const keep = lastGood.current?.token === token ? lastGood.current.value : null;
        setSettled({
          token: started.token,
          value: keep,
          status: 'error',
          stale: keep !== null,
          error: readFailureOf(e),
          pending: null,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [started]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return useMemo(
    () => ({ value: active.value, status: active.status, stale: active.stale, error: active.error, retry }),
    [active.value, active.status, active.stale, active.error, retry],
  );
}
